"use client"
import type React from "react"
import { createContext, useContext, useMemo, useState } from "react"
import type { ThemeVariant } from "../../types"

const DEFAULT_THEME = "light" satisfies ThemeVariant

type ThemeContext = {
  theme: ThemeVariant
  setTheme: React.Dispatch<React.SetStateAction<ThemeVariant>>
}

export const ThemeContext = createContext<ThemeContext>({
  theme: DEFAULT_THEME,
  setTheme: () => null,
})

type ThemeProviderProps = {
  children: React.ReactNode
  initialTheme?: ThemeVariant
  theme?: ThemeVariant
}

const setThemeControlled = () => {
  if (process.env.NODE_ENV === "development") {
    throw new Error(
      "You are trying to setTheme in a controlled mode. This is a noop/redundant operation.",
    )
  }
}

export const ThemeProvider = ({
  children,
  initialTheme = DEFAULT_THEME,
  theme: controlledTheme,
}: ThemeProviderProps) => {
  const [theme, setTheme] = useState(initialTheme)

  const value: ThemeContext = useMemo(() => {
    return controlledTheme
      ? { theme: controlledTheme, setTheme: setThemeControlled }
      : { theme, setTheme }
  }, [theme, controlledTheme])

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
}

export const useTheme = () => {
  return useContext(ThemeContext)
}
